import React, {useEffect, useCallback, useMemo, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  bankPaymentOrderGetId,
  bankPaymentOrderGetAvailablePayments,
  bankPaymentOrderUpdate,
} from '../../../redux/slices/bankpaymentorder.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectBankPaymentOrderData = (state) => ({
  isRedirect: state.bankpaymentorder.isRedirect,
  detail: state.bankpaymentorder.detail,
  loading: state.bankpaymentorder.loading,
  getAvailablePayments: state.bankpaymentorder.getAvailablePayments
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)
  
  const pageUrl = useMemoizedPageUrl(location)

  const {isRedirect, detail, loading, getAvailablePayments} = useSelector(selectBankPaymentOrderData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(bankPaymentOrderGetAvailablePayments())
    dispatch(bankPaymentOrderGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PaymentRequestList: Yup.array()
      .of(
        Yup.object().shape({
          PaymentRequestId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        })
      )
      .min(1, intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    PaymentDate: new Date(),
    PaymentRequestList: [],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(bankPaymentOrderUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  formikRef.current = formik

  useEffect(() => {
    if (getAvailablePayments && getAvailablePayments.length > 0 && detail && formikRef.current && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)

        if (property === 'bankPaymentOrderItems') {
          detail['bankPaymentOrderItems'] && detail['bankPaymentOrderItems'].forEach((item, index) => {
            const matchingPayment = getAvailablePayments.find(
              (payment) => payment.paymentRequestId === item.paymentRequestId
            )

            if (matchingPayment)
              formikRef.current.setFieldValue(
                `PaymentRequestList[${index}][PaymentRequestId]`,
                item.paymentRequestId
              )
          })
        } else {
          for (const values in formValues) {
            if (values === propVal) formikRef.current.setFieldValue(propVal, detail[property])
          }
        }
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, getAvailablePayments, formValues])

  const handlePaymentDateChange = useCallback(([date]) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('PaymentDate', date)
    }
  }, [])

  const handleCheckboxChange = useCallback((event, paymentRequestId) => {
    if (formikRef.current) {
      const isChecked = event.target.checked
      const newPaymentRequestList = isChecked
        ? [...formikRef.current.values.PaymentRequestList, {PaymentRequestId: paymentRequestId}]
        : formikRef.current.values.PaymentRequestList.filter(
            (item) => item.PaymentRequestId !== paymentRequestId
          )
      formikRef.current.setFieldValue('PaymentRequestList', newPaymentRequestList)
    }
  }, [])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER'}),
      path: '/cash/bank-payment-order',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/cash/bank-payment-order/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  const handleCancel = useCallback(() => {
    navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [navigate, pageUrl, id])

  const tableHeaders = useMemo(() => [
    { key: 'PROJECTNAME', className: '' },
    { key: 'COMPANYTITLE', className: '' },
    { key: 'COMPANYBALANCE', className: '' },
    { key: 'REQUESTEDPAYMENT', className: '' },
    { key: 'REQUESTEDDATE', className: '' },
    { key: 'REQUESTEDUSERNAMESURNAME', className: '' },
    { key: 'APPROVEDPAYMENT', className: '' },
    { key: 'APPROVEDDATE', className: '' }
  ], [])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.PAYMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('PaymentDate')}
                    onChange={handlePaymentDateChange}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.PAYMENTREQUESTLIST'})}
                </label>
                <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                  <div className='table-responsive mt-4'>
                    <table className='table align-middle gs-0 gy-4'>
                      <thead>
                        <tr className='fw-bold text-muted bg-light'>
                          <th className='w-25px'>&nbsp;</th>
                          {tableHeaders.map((header) => (
                            <th key={header.key} className={header.className}>
                              {intl.formatMessage({
                                id: `BANKPAYMENTORDER.CREATE.PAYMENTREQUESTLIST.${header.key}`,
                              })}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {getAvailablePayments && getAvailablePayments.map((item, index) => {
                          const isChecked = formik.values.PaymentRequestList && formik.values.PaymentRequestList.some(
                            (ch) => ch.PaymentRequestId === (item && item.paymentRequestId)
                          )

                          return (
                            <tr key={index}>
                              <td>
                                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                  <input
                                    className='form-check-input widget-9-check'
                                    type='checkbox'
                                    value={item && item.paymentRequestId}
                                    checked={isChecked}
                                    onChange={(event) =>
                                      handleCheckboxChange(event, item && item.paymentRequestId)
                                    }
                                  />
                                </div>
                              </td>
                              <td>
                                <div className='d-flex align-items-center'>
                                  <div className='d-flex justify-content-start flex-column'>
                                    <div className='text-dark fw-bold fs-6'>
                                      {item && item.projectName}
                                    </div>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                      {item && item.name}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className='text-muted fw-semibold'>{item && item.companyTitle}</td>
                              <td className='text-muted fw-semibold'>
                                {intl.formatNumber(item && item.companyBalance, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </td>
                              <td className='text-muted fw-semibold'>
                                {intl.formatNumber(item && item.requestedPayment, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </td>
                              <td className='text-muted fw-semibold'>
                                {intl.formatDate(new Date(item && item.requestedDate), {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </td>
                              <td className='text-muted fw-semibold'>
                                {item && item.requestedUserNameSurname}
                              </td>
                              <td className='text-muted fw-semibold'>
                                {intl.formatNumber(item && item.approvedPayment, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </td>
                              <td className='text-muted fw-semibold'>
                                {intl.formatDate(new Date(item && item.approvedDate), {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={handleCancel}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {Edit}
